import { Switch } from '@thanx/uikit/switch'
import { useCallback } from 'react'
import Markdown from 'react-markdown'

import ThanxAICallout from 'components/AI/ThanxAICallout'
import ThanxAIPickerV2 from 'components/AI/ThanxAIPickerV2'
import ThanxAISuggestionChip from 'components/AI/ThanxAISuggestionChip'
import { useAIConversationActions } from 'hooks/useAIConversationActions'
import useFlag from 'hooks/useFlag'
import ThanxAISegmentVisualization from 'scenes/Segments/AISegmentBuilder/ThanxAISegmentVisualization'
import type { PickerSubmissionPayload } from 'slices/aiConversations/pickerTypes'
import type { Message } from 'slices/aiConversations/types'

import './AIMessage.scss'

export interface AIMessageProps {
  message: Message
  showLabel?: boolean
  isStreaming?: boolean
  isLatestDraftSegment?: boolean
  isLatestSavedSegment?: boolean
  hasAnySavedSegment?: boolean
  onCalloutAction?: (message: string) => void
  calloutActionsDisabled?: boolean
}

const AIMessage = ({
  message,
  showLabel = true,
  isStreaming: _isStreaming = false, // Unused parameter for future functionality
  isLatestDraftSegment = false,
  isLatestSavedSegment = false,
  hasAnySavedSegment = false,
  onCalloutAction,
  calloutActionsDisabled = false,
}: AIMessageProps) => {
  const { onSuggestionClick, onPickerSubmit } = useAIConversationActions()

  // Feature flag to control picker visibility - set to false by default until launch
  const aiPickerEnabled = useFlag('ai-picker-enabled', false)

  const handlePickerSubmit = useCallback(
    (payload: PickerSubmissionPayload) => {
      onPickerSubmit(message.id, payload)
    },
    [message.id, onPickerSubmit]
  )

  const renderContent = () => {
    // Only render markdown for AI messages, never for user messages
    if (message.type === 'ai' && message.isMarkdown) {
      return <Markdown>{message.content}</Markdown>
    }
    return message.content
  }

  const hasSingleVisualization = Boolean(message.segmentVisualization)
  const hasMultipleVisualizations = Boolean(
    message.segmentVisualizations && message.segmentVisualizations.length > 0
  )
  const hasSuggestions = Boolean(
    message.suggestions && message.suggestions.length > 0
  )
  const hasPickerData = Boolean(message.pickerData)
  const hasCallouts = Boolean(message.callouts && message.callouts.length > 0)

  const renderMultipleVisualizations = () => {
    if (
      !message.segmentVisualizations ||
      message.segmentVisualizations.length === 0
    ) {
      return null
    }
    return (
      <>
        {message.segmentVisualizations.map((visualization, index) => (
          <ThanxAISegmentVisualization
            key={`visualization-${index}`}
            data={visualization}
            instanceId={`${message.id}-${index}`}
            isLatestDraftSegment={isLatestDraftSegment}
            isLatestSavedSegment={isLatestSavedSegment}
            hasAnySavedSegment={hasAnySavedSegment}
          />
        ))}
      </>
    )
  }

  const renderSuggestions = () => {
    if (!message.suggestions) {
      return null
    }
    return (
      <>
        {message.suggestions.map((suggestion, index) => (
          <ThanxAISuggestionChip
            key={index}
            suggestion={suggestion}
            onClick={onSuggestionClick}
            inline={true}
          />
        ))}
      </>
    )
  }

  return (
    <div className="thanx-ai-message">
      <Switch condition={showLabel}>
        <div className="thanx-ai-message__label">ThanxAI</div>
      </Switch>
      <div className="thanx-ai-message__content markdown-content">
        {renderContent()}
      </div>
      <Switch condition={hasCallouts}>
        {message.callouts?.map(callout => (
          <ThanxAICallout
            key={callout.id}
            callout={callout}
            onActionClick={onCalloutAction}
            actionsDisabled={calloutActionsDisabled}
          />
        ))}
      </Switch>
      <Switch condition={hasSingleVisualization}>
        <ThanxAISegmentVisualization
          data={message.segmentVisualization!}
          instanceId={`${message.id}-0`}
          isLatestDraftSegment={isLatestDraftSegment}
          isLatestSavedSegment={isLatestSavedSegment}
          hasAnySavedSegment={hasAnySavedSegment}
        />
      </Switch>
      <Switch condition={hasMultipleVisualizations}>
        {renderMultipleVisualizations()}
      </Switch>
      <Switch condition={hasSuggestions}>
        <div className="thanx-ai-message__suggestions">
          {renderSuggestions()}
        </div>
      </Switch>
      <Switch condition={hasPickerData && aiPickerEnabled}>
        <div className="thanx-ai-message__picker">
          <ThanxAIPickerV2
            data={message.pickerData!}
            onSubmit={handlePickerSubmit}
            isSubmitted={message.pickerSubmitted}
            messageId={message.id}
          />
        </div>
      </Switch>
    </div>
  )
}

export default AIMessage
